import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import {
  Line,
  CreateUpdateInvoiceLine,
  lineT,
  computeInvoiceLinesRequestT,
} from "../deliveries/types";
import { selectInvoiceLinesForCreate } from "../deliveries/selectors";

/**
 * In-memory invoice lines used while creating a new cargo, before any delivery
 * exists on the server. The surcharge behaviour is kept identical to the server
 * by computing the lines through the stateless `/api/invoice-lines/compute`
 * endpoint (see `useInvoiceLines`); this slice just holds the latest result.
 *
 * It is ephemeral: clear it when the create-shipment dialog closes.
 */
interface DraftInvoiceLinesState {
  lines: Line[];
}

const initialState: DraftInvoiceLinesState = {
  lines: [],
};

const slice = createSlice({
  name: "data/draft-invoice-lines",
  initialState,
  reducers: {
    // Replace the whole list with the recomputed set returned by the server.
    draftLinesSet: (state, action: PayloadAction<Line[]>) => {
      state.lines = action.payload;
    },
    // Reordering has no effect on surcharges, so it stays purely client-side.
    draftLineReordered: (
      state,
      action: PayloadAction<{ lineId: string; toIndex: number }>
    ) => {
      const from = state.lines.findIndex((l) => l.id === action.payload.lineId);
      if (from === -1) {
        return;
      }
      const [moved] = state.lines.splice(from, 1);
      state.lines.splice(action.payload.toIndex, 0, moved);
    },
    draftLinesCleared: (state) => {
      state.lines = [];
    },
    // Mirrors the server's copy-with-reset-quantity semantics (see
    // copy-delivery-lines.ts): only the quantity is touched, the line's
    // amount is left as-is.
    draftLinesQuantitiesReset: (state) => {
      state.lines = state.lines.map((l) => ({ ...l, quantity: "1" }));
    },
  },
});

export default slice.reducer;
export const {
  draftLinesSet,
  draftLineReordered,
  draftLinesCleared,
  draftLinesQuantitiesReset,
} = slice.actions;

const prefix = "data/draft-invoice-lines";

export type ComputeDraftOp =
  | { type: "ADD"; line: CreateUpdateInvoiceLine }
  | { type: "UPDATE"; id: string; line: CreateUpdateInvoiceLine }
  | { type: "UPDATE_SURCHARGE"; id: string; percentage: string }
  | { type: "DELETE"; lineId: string };

/**
 * Recompute the draft line set through the server (so surcharge behaviour
 * matches a persisted delivery exactly) and store the result. Returns the
 * recomputed lines.
 */
export const computeDraftLines = createErrorReportingAsyncThunk(
  `${prefix}/compute`,
  async (input: { clientId: string | null; op: ComputeDraftOp }, thunkAPI) => {
    const lines = selectInvoiceLinesForCreate()(thunkAPI.getState());
    const result = await axios
      .post(
        "/api/deliveries/compute-invoice-lines",
        computeInvoiceLinesRequestT.encode({
          clientId: input.clientId,
          lines,
          op: input.op,
        })
      )
      .then(getData)
      .then(tPromise.decode(t.array(lineT)));
    thunkAPI.dispatch(draftLinesSet(result));
    const before = new Set(lines.map((l) => l.id));
    return result.filter((l) => !before.has(l.id)).map((l) => l.id);
  }
);
